import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Check as CompleteIcon,
  Pause as HoldIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  ArrowBack as ArrowBackIcon,
  Science as ChemoIcon
} from '@mui/icons-material';
import { useUserData } from '../../hooks/useUserData';
import { getChemotherapyCycles, updateChemotherapyCycle, completeChemotherapyCycle } from '../../services/chemotherapyService';
import { CHEMO_CYCLE_STATUS_COLORS } from '../../types/enums';
import { HeaderCard, PageContainer, ContentCard } from './oncologyStyles';

const ChemotherapyCycleTracker = ({ patientId: propPatientId, treatmentPlanId, onCycleUpdate, embedded = false }) => {
  const { patientId: urlPatientId } = useParams();
  const navigate = useNavigate();
  const patientId = propPatientId || urlPatientId;
  const { defaultFacility } = useUserData();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);

  const loadCycles = useCallback(async () => {
    if (!defaultFacility?.id || !patientId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await getChemotherapyCycles(patientId, defaultFacility.id, {
        treatmentPlanId
      });
      setCycles(result.data || []);
    } catch (err) {
      console.error('Error loading chemotherapy cycles:', err);
      setError('Failed to load chemotherapy cycles');
    } finally {
      setLoading(false);
    }
  }, [defaultFacility?.id, patientId, treatmentPlanId]);

  useEffect(() => {
    loadCycles();
  }, [loadCycles]);

  const getActiveStep = () => {
    const completedCount = cycles.filter(c => c.status === 'Completed').length;
    return completedCount;
  };

  const calculateProgress = () => {
    if (cycles.length === 0) return 0;
    const completed = cycles.filter(c => c.status === 'Completed').length;
    return (completed / cycles.length) * 100;
  };

  const handleStartCycle = async (cycle) => {
    try {
      await updateChemotherapyCycle(cycle.id, {
        status: 'In Progress',
        actualStartDate: new Date().toISOString()
      });
      loadCycles();
      if (onCycleUpdate) onCycleUpdate();
    } catch (err) {
      setError('Failed to start cycle');
    }
  };

  const handleCompleteCycle = async (cycle) => {
    try {
      await completeChemotherapyCycle(cycle.id);
      loadCycles();
      if (onCycleUpdate) onCycleUpdate();
    } catch (err) {
      setError('Failed to complete cycle');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completed': return <CompleteIcon color="success" />;
      case 'In Progress': return <CircularProgress size={20} />;
      case 'Scheduled': return <ScheduleIcon color="info" />;
      case 'Delayed': return <WarningIcon color="warning" />;
      case 'Held': return <HoldIcon color="error" />;
      default: return <ScheduleIcon />;
    }
  };

  const cycleContent = (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : cycles.length === 0 ? (
        <Alert severity="info">
          No chemotherapy cycles scheduled for this treatment plan.
        </Alert>
      ) : (
        <>
          {/* Progress Overview */}
          <Card variant="outlined" sx={{ mb: 3, borderRadius: '12px' }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Treatment Progress
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={calculateProgress()} 
                      sx={{ flex: 1, height: 10, borderRadius: 5 }}
                    />
                    <Typography variant="h6">
                      {cycles.filter(c => c.status === 'Completed').length} / {cycles.length}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip 
                      label={`${cycles.filter(c => c.status === 'Completed').length} Completed`} 
                      color="success" 
                      size="small" 
                    />
                    <Chip 
                      label={`${cycles.filter(c => c.status === 'In Progress').length} In Progress`} 
                      color="warning" 
                      size="small" 
                    />
                    <Chip 
                      label={`${cycles.filter(c => c.status === 'Scheduled').length} Scheduled`} 
                      color="info" 
                      size="small" 
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Cycle Timeline */}
          <Stepper activeStep={getActiveStep()} orientation="vertical">
            {cycles.map((cycle, index) => (
              <Step key={cycle.id} completed={cycle.status === 'Completed'}>
                <StepLabel
                  optional={
                    <Typography variant="caption">
                      {formatDate(cycle.actualStartDate || cycle.plannedStartDate)}
                    </Typography>
                  }
                  icon={getStatusIcon(cycle.status)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Cycle {cycle.cycleNumber}
                    <Chip 
                      label={cycle.status} 
                      size="small" 
                      color={CHEMO_CYCLE_STATUS_COLORS[cycle.status] || 'default'}
                    />
                  </Box>
                </StepLabel>
                <StepContent>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Planned:</strong> {formatDate(cycle.plannedStartDate)}
                      {cycle.actualStartDate && (
                        <> • <strong>Actual:</strong> {formatDate(cycle.actualStartDate)}</>
                      )}
                    </Typography>
                    {cycle.bsaValue && (
                      <Typography variant="body2">
                        <strong>BSA:</strong> {cycle.bsaValue} m²
                      </Typography>
                    )}
                    {cycle.doseModification && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Dose reduced by {cycle.doseModification.percentReduction}% - {cycle.doseModification.reason}
                      </Alert>
                    )}
                    {cycle.delay && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Delayed {cycle.delay.daysDelayed} days - {cycle.delay.reason}
                      </Alert>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {cycle.status === 'Scheduled' && (
                      <Button 
                        variant="contained" 
                        size="small" 
                        startIcon={<StartIcon />}
                        onClick={() => handleStartCycle(cycle)}
                      >
                        Start Cycle
                      </Button>
                    )}
                    {cycle.status === 'In Progress' && (
                      <Button 
                        variant="contained" 
                        size="small" 
                        color="success"
                        startIcon={<CompleteIcon />}
                        onClick={() => handleCompleteCycle(cycle)}
                      >
                        Complete
                      </Button>
                    )}
                    <Tooltip title="Edit Cycle">
                      <IconButton size="small" onClick={() => { setSelectedCycle(cycle); setActionDialogOpen(true); }}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </>
      )}

      {/* Edit Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Cycle {selectedCycle?.cycleNumber}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ pt: 2 }}>
            Cycle editing functionality - allows modifying dates, applying dose reductions, recording delays, etc.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
          <Button variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>
    </>
  );

  // Show message if no patient selected (standalone mode)
  if (!patientId && !embedded) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <HeaderCard>
            <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item>
                  <IconButton onClick={() => navigate(-1)} sx={{ color: 'white', mr: 1 }}>
                    <ArrowBackIcon />
                  </IconButton>
                </Grid>
                <Grid item>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <ChemoIcon sx={{ fontSize: 40, opacity: 0.9 }} />
                    <Box>
                      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Chemotherapy Schedule
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Cycle tracking and progress management
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </HeaderCard>

          <ContentCard>
            <CardContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                Please select a patient to view or manage chemotherapy cycles.
              </Alert>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                To view chemotherapy cycles, navigate to a patient's profile and use the Oncology tab.
              </Typography>
              <Button variant="outlined" onClick={() => navigate('/patients')}>
                Go to Patients
              </Button>
            </CardContent>
          </ContentCard>
        </Container>
      </PageContainer>
    );
  }

  // Embedded mode
  if (embedded) {
    return (
      <ContentCard>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ChemoIcon color="info" />
            <Typography variant="h6" fontWeight="bold">
              Chemotherapy Cycle Tracker
            </Typography>
          </Box>
          {cycleContent}
        </CardContent>
      </ContentCard>
    );
  }

  // Standalone page with patient
  return (
    <PageContainer>
      <Container maxWidth="lg">
        <HeaderCard>
          <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ChemoIcon sx={{ fontSize: 40, opacity: 0.9 }} />
              <Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Chemotherapy Cycle Tracker
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Cycle tracking and progress management
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </HeaderCard>

        <ContentCard>
          <CardContent>
            {cycleContent}
          </CardContent>
        </ContentCard>
      </Container>
    </PageContainer>
  );
};

export default ChemotherapyCycleTracker;
